import type { Question } from "../types";
import type { AnswerInput, ProblemProfile, WeightedTag } from "./types";

/**
 * UC-2a. Sum each answered option's tag weights per tag, normalize against the top-scoring tag,
 * then drop anything under tagInclusionFloor. Exactly the algorithm worked through in
 * docs/design/2026-08-24-use-case-decomposition.md Section 5.3: a safety filter for UC-2b, not a
 * ranking (ranking is UC-2c's job, over the visitor's verbatim words, not this arithmetic).
 *
 * Multi-select questions contribute full weight from every selected option (decided 2026-08-24,
 * decomposition doc 5.2) — not divided across selections.
 *
 * Pure function: no I/O, no model call, fixture-tested against the decomposition doc's worked
 * numbers so a passing test here is corroborated, not just internally consistent.
 */
export function deriveProblemProfile(answers: AnswerInput[], questions: Question[], tagInclusionFloor: number): ProblemProfile {
  const questionsById = new Map(questions.map((q) => [q.id, q]));
  const sums = new Map<string, number>();

  for (const answer of answers) {
    const question = questionsById.get(answer.questionId);
    if (!question) continue;
    // A question flagged excludeFromFiltering (e.g. working style) is still asked and still
    // reaches the UC-2c prompt (see rank-and-reason/prompt.ts), but never contributes tag weight
    // here — it must have zero effect on which Board Members become candidates.
    if (question.excludeFromFiltering) continue;
    const optionsById = new Map(question.options.map((o) => [o.id, o]));
    for (const optionId of answer.selectedOptionIds) {
      const option = optionsById.get(optionId);
      if (!option) continue;
      for (const tw of option.tagWeights) {
        sums.set(tw.tagId, (sums.get(tw.tagId) ?? 0) + tw.weight);
      }
    }
  }

  const topWeight = Math.max(0, ...sums.values());
  const weightedTags: WeightedTag[] = [];
  if (topWeight > 0) {
    for (const [tagId, sum] of sums) {
      const normalized = sum / topWeight;
      if (normalized >= tagInclusionFloor) {
        weightedTags.push({ tagId, weight: normalized });
      }
    }
  }

  return { weightedTags };
}
